'use client';

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Separator } from '@/components/ui/separator';
import { AnalyticsEvent } from '@/enums/analytics-type';
import { useDataAnalytics } from '@/hooks/analytics/analytics';
import { dataTestTransformer } from '@/utils/data-test-helper';
import React, { useEffect, useState } from 'react';
import { FilterComponent } from './filter';

type FilterItem = {
  displayName: string;
  id: string;
  selected: boolean;
};

export type FiltersGrouped = Record<string, FilterItem[]>;

const emptyRadioOptions: FilterItem[] = [];

type FilterDialogProps = {
  asAccordion?: boolean;
  children: React.ReactNode;
  filters: FiltersGrouped;
  groupOrder?: string[];
  onChange?: (next: FiltersGrouped) => void;
  onRadioChange?: (next: FilterItem[]) => void;
  radioOptions?: FilterItem[];
  radioOptionsTitle?: string;
  showCloseButton?: boolean;
  showTitle?: boolean;
  withSeparator?: boolean;
};

export const FilterDialog = ({
  asAccordion = false,
  children,
  filters,
  groupOrder = [],
  onChange,
  onRadioChange,
  radioOptions = emptyRadioOptions,
  radioOptionsTitle,
  showCloseButton = true,
  showTitle = true,
  withSeparator = false,
}: FilterDialogProps): React.JSX.Element => {
  const [open, setOpen] = useState(false);
  const [localFilters, setLocalFilters] = useState<FiltersGrouped>(() => structuredClone(filters));
  const [localRadioOptions, setLocalRadioOptions] = useState<FilterItem[]>(() => structuredClone(radioOptions));
  const { trackEvent } = useDataAnalytics();

  useEffect(() => {
    setLocalFilters(structuredClone(filters));
    setLocalRadioOptions(structuredClone(radioOptions));
  }, [filters, open, radioOptions]);

  const toggle = (group: string, id: string, value: boolean): void => {
    setLocalFilters((prev) => {
      const nextGroup = prev[group].map(item => item.id === id ? { ...item, selected: value } : item);
      return { ...prev, [group]: nextGroup };
    });
  };

  const handleApply = (): void => {
    const selectedFilters: string[] = [];
    localRadioOptions.filter(item => item.selected).forEach((item) => {
      selectedFilters.push(item.displayName);
    });
    Object.values(localFilters).forEach((items) => {
      items.filter(item => item.selected).forEach((item) => {
        selectedFilters.push(item.displayName);
      });
    });

    trackEvent(AnalyticsEvent.SORT_FILTER_SELECT, { sortFilterSelection: selectedFilters.join(', ') });
    onChange?.(localFilters);
    onRadioChange?.(localRadioOptions);
    setOpen(false);
  };

  const handleClear = (): void => {
    const cleared: FiltersGrouped = {};
    Object.entries(localFilters).forEach(([group, items]) => {
      cleared[group] = items.map(item => ({ ...item, selected: false }));
    });
    setLocalFilters(cleared);
  };

  const handleRadioChange = (id: string): void => {
    setLocalRadioOptions(previousOptions => previousOptions.map(item => ({ ...item, selected: item.id === id })));
  };

  const filterGroups = Object.entries(localFilters);
  const orderedFilterGroups = groupOrder.length
    ? [
      ...groupOrder.flatMap(groupName => filterGroups.filter(([currentGroupName]) => currentGroupName === groupName)),
      ...filterGroups.filter(([currentGroupName]) => !groupOrder.includes(currentGroupName)),
    ]
    : filterGroups;
  const hasRadioOptions = !!localRadioOptions.length;

  return (
    <Dialog open={open}
      onOpenChange={setOpen}
    >
      <DialogTrigger asChild>{children}</DialogTrigger>

      <DialogContent className="flex max-h-[calc(100dvh-2rem)] w-full flex-col md:min-w-[430px] md:max-w-[430px]">
        <DialogHeader className="shrink-0"
          showCloseButton={showCloseButton}
        >
          <DialogTitle className={showTitle ? undefined : 'hidden'}
            data-test="geo-location-error-title"
          >
            Filter Results
          </DialogTitle>
          <DialogDescription className="hidden" />
        </DialogHeader>

        <div className="min-h-0 overflow-y-auto flex flex-col gap-md">
          {hasRadioOptions &&
            <div className="flex flex-col gap-md">
              {radioOptionsTitle &&
                <h3 data-test="radio-options-title">{radioOptionsTitle}</h3>
              }
              <RadioGroup aria-label="Sort options"
                className="flex flex-col gap-sm"
                value={localRadioOptions.find(item => item.selected)?.id}
                onValueChange={handleRadioChange}
              >
                {localRadioOptions.map(item =>
                  <div key={item.id}
                    className="flex items-center gap-2"
                  >
                    <RadioGroupItem data-test={`radio-${dataTestTransformer(item.displayName)}`}
                      id={`radio-${dataTestTransformer(item.displayName)}`}
                      value={item.id}
                    />
                    <label className="cursor-pointer font-normal text-sm"
                      htmlFor={`radio-${dataTestTransformer(item.displayName)}`}
                    >
                      {item.displayName}
                    </label>
                  </div>
                )}
              </RadioGroup>
              {withSeparator && !!orderedFilterGroups.length && <Separator />}
            </div>
          }

          {asAccordion &&
            <Accordion type="multiple">
              {orderedFilterGroups.map(([groupName, items]) =>
                <AccordionItem key={groupName}
                  value={groupName}
                >
                  <AccordionTrigger className="py-sm"
                    data-test={`filter-type-${dataTestTransformer(groupName)}`}
                    iconClassName="text-primary size-6"
                  >
                    <h3 data-test={`filter-type-title-${dataTestTransformer(groupName)}`}>{groupName}</h3>
                  </AccordionTrigger>
                  <AccordionContent className="flex flex-col gap-ms">
                    {items.map(item =>
                      <FilterComponent key={item.id}
                        checked={!!item.selected}
                        displayName={item.displayName}
                        onToggle={(value: boolean) => toggle(groupName, item.id, value)}
                      />
                    )}
                  </AccordionContent>
                </AccordionItem>
              )}
            </Accordion>
          }

          {!asAccordion &&
            orderedFilterGroups.map(([groupName, items], index) =>
              <React.Fragment key={groupName}>
                <div className="flex flex-col gap-ms">
                  <h4 data-test={`filter-type-${dataTestTransformer(groupName)}`}>
                    {groupName}
                  </h4>

                  <div className="flex flex-col gap-ms">
                    {items.map(item =>
                      <FilterComponent key={item.id}
                        checked={!!item.selected}
                        displayName={item.displayName}
                        onToggle={(value: boolean) => toggle(groupName, item.id, value)}
                      />
                    )}
                  </div>
                </div>
                {withSeparator && index < orderedFilterGroups.length - 1 && <hr />}
              </React.Fragment>
            )
          }
        </div>

        <DialogFooter className="grid shrink-0 grid-cols-2 gap-ms">
          <Button data-test="clear-filters-button"
            variant="outline"
            onClick={handleClear}
          >
            Clear Filters
          </Button>
          <Button data-test="apply-filters-button"
            onClick={handleApply}
          >
            Apply
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
